import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Loader2, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";
import client from "../lib/api";
import { GradeBadge, ActionBadge, EarningsDot } from "../components/Badges";
import { num } from "../lib/format";

const GRADES = ["A+", "Strong", "Watch", "Weak", "Avoid"];

export default function Scanner() {
  const nav = useNavigate();
  const [direction, setDirection] = useState("long");
  const [grade, setGrade] = useState("");
  const [setup, setSetup] = useState("");
  const [setupOptions, setSetupOptions] = useState([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/scanner", { params: { direction, grade, setup, active_only: activeOnly } });
      setReady(data.ready);
      setScanning(data.scanning);
      setRows(data.results || []);
      setMeta(data.meta || null);
    } catch (e) {
      setReady(false);
    } finally {
      setLoading(false);
    }
  }, [direction, grade, setup, activeOnly]);

  useEffect(() => { load(); }, [load]);

  // setup filter options are direction-specific; reset the selection on direction change
  useEffect(() => {
    setSetup("");
    client.get("/meta/filters", { params: { direction } })
      .then(({ data }) => setSetupOptions(data.setups || []))
      .catch(() => setSetupOptions([]));
  }, [direction]);

  // poll while data is loading in background
  useEffect(() => {
    if (ready) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [ready, load]);

  const refresh = async () => {
    await client.post("/scan/refresh").catch(() => {});
    load();
  };

  return (
    <div className="px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-head text-2xl font-black tracking-tight" data-testid="scanner-title">SCANNER</h1>
          <p className="mt-1 text-sm text-gray-500">
            {ready ? `${rows.length} symbols · ${direction.toUpperCase()} bias` : "S&P 500 swing-trade quality ranking"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-sm border border-white/10">
            <ToggleBtn active={direction === "long"} onClick={() => setDirection("long")} testid="dir-long">
              <TrendingUp size={14} /> LONG
            </ToggleBtn>
            <ToggleBtn active={direction === "short"} onClick={() => setDirection("short")} testid="dir-short">
              <TrendingDown size={14} /> SHORT
            </ToggleBtn>
          </div>
          <button data-testid="refresh-btn" onClick={refresh}
            className="flex items-center gap-1.5 rounded-sm border border-white/10 px-3 py-1.5 text-xs text-gray-400 transition-colors duration-200 hover:border-white/20 hover:text-white">
            <RefreshCw size={13} className={scanning ? "animate-spin" : ""} /> Rescan
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Chip label={activeOnly ? "● Active only" : "Active only"} active={activeOnly} onClick={() => setActiveOnly(!activeOnly)} testid="active-only" />
        <span className="mx-1 h-4 w-px bg-white/10" />
        <Chip label="All grades" active={grade === ""} onClick={() => setGrade("")} testid="grade-all" />
        {GRADES.map((g) => (
          <Chip key={g} label={g} active={grade === g} onClick={() => setGrade(grade === g ? "" : g)} testid={`grade-${g}`} />
        ))}
        <span className="mx-1 h-4 w-px bg-white/10" />
        {setupOptions.map((s) => (
          <Chip key={s} label={s} active={setup === s} onClick={() => setSetup(setup === s ? "" : s)} testid={`setup-${s}`} />
        ))}
      </div>

      {!ready ? (
        <Loading scanning={scanning} />
      ) : (
        <div className="mt-4 max-h-[calc(100vh-210px)] overflow-auto border border-white/10">
          <table className="w-full border-collapse" data-testid="scanner-table">
            <thead className="sticky top-0 z-10 bg-[#111111]">
              <tr className="text-left font-mono-sg text-[11px] uppercase tracking-wider text-gray-500">
                <Th className="w-12 text-right">#</Th>
                <Th>Ticker</Th>
                <Th className="text-right">Score</Th>
                <Th>Grade</Th>
                <Th>Setup</Th>
                <Th className="text-right">RS</Th>
                <Th className="text-right">Trend</Th>
                <Th className="text-right">RVOL</Th>
                <Th>Earnings</Th>
                <Th>Action</Th>
                <Th className="w-8" />
              </tr>
            </thead>
            <tbody className="font-mono-sg text-sm">
              {rows.map((r) => (
                <tr
                  key={r.ticker}
                  data-testid="scanner-row"
                  onClick={() => nav(`/stock/${r.ticker}?direction=${direction}`)}
                  className="cursor-pointer border-t border-white/5 transition-colors duration-150 hover:bg-white/5"
                >
                  <Td className="text-right text-gray-600">{r.rank}</Td>
                  <Td>
                    <div className="font-bold text-white">{r.ticker}</div>
                    <div className="max-w-[140px] truncate text-[11px] font-normal text-gray-600">{r.name}</div>
                  </Td>
                  <Td className="text-right font-bold" style={{ color: scoreColor(r.score) }}>{num(r.score, 0)}</Td>
                  <Td><GradeBadge grade={r.grade} gradeKey={r.grade_key} /></Td>
                  <Td className="text-gray-300">{r.setup_type || r.setup_label}</Td>
                  <Td className="text-right" style={{ color: rsColor(r.rs_rating) }}>{r.rs_rating}</Td>
                  <Td className="text-right text-gray-300">{r.trend_q}</Td>
                  <Td className="text-right" style={{ color: r.rvol >= 1.5 ? "#00FF66" : r.rvol >= 1 ? "#FFD700" : "#666" }}>
                    {num(r.rvol, 2)}
                  </Td>
                  <Td><EarningsDot level={r.earnings_level} /></Td>
                  <Td>
                    <div className="flex items-center gap-1.5">
                      <ActionBadge action={r.action} />
                      {r.signal_status === "EXPIRING" && (
                        <span className="font-mono-sg text-[10px] text-[#FF7F00]" title="Entry window closing">{r.expires_in}d left</span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-gray-700"><ChevronRight size={15} /></Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={11} className="py-16 text-center text-sm text-gray-400">
                  {activeOnly && direction === "short"
                    ? "No active short signals — the market regime is bullish (SPY above its 200-day), so the model suppresses shorts against strength. Switch to LONG, or toggle off \"Active only\" to inspect the universe."
                    : activeOnly
                    ? "No active signals right now. Toggle off \"Active only\" to see the full universe."
                    : "No symbols match these filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function scoreColor(s) {
  if (s >= 90) return "#00F0FF";
  if (s >= 80) return "#00FF66";
  if (s >= 70) return "#FFD700";
  if (s >= 60) return "#FF7F00";
  return "#FF3B30";
}
function rsColor(r) {
  if (r >= 70) return "#00FF66";
  if (r >= 50) return "#FFD700";
  return "#FF7F00";
}

const Th = ({ children, className = "" }) => <th className={`px-3 py-2.5 font-medium ${className}`}>{children}</th>;
const Td = ({ children, className = "", style }) => <td className={`px-3 py-2.5 align-middle ${className}`} style={style}>{children}</td>;

function ToggleBtn({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 font-mono-sg text-xs font-bold transition-colors duration-200 ${active ? "bg-white/10 text-white" : "text-gray-500 hover:text-gray-300"}`}>
      {children}
    </button>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`rounded-sm border px-2.5 py-1 font-mono-sg text-xs transition-colors duration-200 ${active ? "border-[#00F0FF]/40 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/10 text-gray-500 hover:border-white/20 hover:text-gray-300"}`}>
      {label}
    </button>
  );
}

function Loading({ scanning }) {
  return (
    <div className="mt-4 flex flex-col items-center justify-center gap-3 border border-white/10 py-24" data-testid="scanner-loading">
      <Loader2 size={28} className="animate-spin text-[#00F0FF]" />
      <p className="font-mono-sg text-sm text-gray-400 sg-blink">
        {scanning ? "INGESTING S&P DAILY/WEEKLY OHLCV…" : "PREPARING SCAN ENGINE…"}
      </p>
      <p className="font-mono-sg text-xs text-gray-600">computing SMA · EMA · RSI · MACD · ADX · ATR · OBV · RS</p>
    </div>
  );
}
